/**
 * MediaPickerModal — Unified media picker with tabs, filters, grouping, multi-select.
 * Used by CreatePost, CreateThread, and anywhere media selection is needed.
 *
 * - Pass `source` to lock to Private or Shared (no tabs shown)
 * - Omit `source` to show Private/Shared tabs
 * - Set `multiSelect` for batch selection with bottom action bar
 */

import { useState, useEffect } from 'react'
import { X, Video, Upload, MapPin, Calendar, CheckCircle, ArrowUpDown, Loader2, Check } from 'lucide-react'
import { useMediaGallery, MediaItem } from '../hooks/useMediaGallery'

export interface SelectedMedia {
  id: string
  url: string
  thumbnail: string
  type: 'image' | 'video'
  onRecordCode?: string
}

interface MediaPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (media: SelectedMedia | SelectedMedia[]) => void
  source?: 'private' | 'shared'  // omit for tabbed mode
  multiSelect?: boolean
}

const PREVIEW_COUNT = 4

export default function MediaPickerModal({ isOpen, onClose, onSelect, source, multiSelect }: MediaPickerModalProps) {
  const [activeTab, setActiveTab] = useState<'private' | 'shared'>(source || 'private')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const gallery = useMediaGallery()

  useEffect(() => {
    if (isOpen) {
      gallery.loadMedia(activeTab)
      setSelected(new Set())
    }
  }, [isOpen, activeTab])

  useEffect(() => {
    if (source) setActiveTab(source)
  }, [source])

  const toSelectedMedia = (item: MediaItem): SelectedMedia => ({
    id: item.id,
    url: item.mediaUrl,
    thumbnail: item.thumbnailUrl,
    type: item.mediaType === 'video' ? 'video' : 'image',
    onRecordCode: item.onRecordCode,
  })

  const handleItemClick = (item: MediaItem) => {
    if (multiSelect) {
      setSelected(prev => {
        const next = new Set(prev)
        next.has(item.id) ? next.delete(item.id) : next.add(item.id)
        return next
      })
    } else {
      onSelect(toSelectedMedia(item))
    }
  }

  const handleBatchSelect = () => {
    const items = gallery.filtered
      .filter(m => selected.has(m.id))
      .map(toSelectedMedia)
    onSelect(items)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Tabs (only when source not locked) */}
              {!source && (
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
                  {(['private', 'shared'] as const).map(tab => (
                    <button key={tab} onClick={() => { setActiveTab(tab); gallery.setExpandedGroups(new Set()) }}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        activeTab === tab ? 'bg-onrecord-orange text-white' : 'text-gray-600 hover:bg-gray-200'
                      }`}>
                      {tab === 'private' ? 'Private' : 'Shared'}
                    </button>
                  ))}
                </div>
              )}
              <div>
                <h2 className="text-lg font-bold">
                  {source ? (source === 'private' ? 'Private Media' : 'Shared with You') : ''}
                </h2>
                <p className="text-sm text-gray-500">{gallery.filtered.length} items</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
              {(['all', 'photo', 'video'] as const).map(t => (
                <button key={t} onClick={() => gallery.setFilter(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    gallery.filter === t ? 'bg-onrecord-orange text-white' : 'text-gray-600 hover:bg-gray-200'
                  }`}>
                  {t === 'all' ? 'All' : t === 'photo' ? 'Photos' : 'Videos'}
                </button>
              ))}
            </div>
            <button onClick={() => gallery.setSort(s => s === 'newest' ? 'oldest' : 'newest')}
              className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200">
              <ArrowUpDown className="w-3 h-3" />{gallery.sort === 'newest' ? 'Newest' : 'Oldest'}
            </button>
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
              {([
                { key: 'date-location', label: 'Date+Place' },
                { key: 'location', label: 'Place' },
                { key: 'date', label: 'Date' },
                { key: 'none', label: 'Flat' },
              ] as const).map(({ key, label }) => (
                <button key={key} onClick={() => { gallery.setGroupBy(key); gallery.setExpandedGroups(new Set()) }}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    gallery.groupBy === key ? 'bg-gray-700 text-white' : 'text-gray-600 hover:bg-gray-200'
                  }`}>{label}</button>
              ))}
            </div>
            {gallery.locations.length > 1 && (
              <select value={gallery.locationFilter} onChange={e => gallery.setLocationFilter(e.target.value)}
                className="px-3 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100 border-0 outline-none cursor-pointer">
                <option value="">All locations</option>
                {gallery.locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {gallery.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-onrecord-orange animate-spin" />
            </div>
          ) : gallery.filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Upload className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-1">{activeTab === 'private' ? 'No private media' : 'No shared media'}</p>
              <p className="text-sm">{activeTab === 'private' ? 'Capture from the on record mobile app' : 'Media shared with you will appear here'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(gallery.groups).map(([key, group]) => {
                const isExpanded = gallery.expandedGroups.has(key)
                const visible = isExpanded ? group.items : group.items.slice(0, PREVIEW_COUNT)
                const hasMore = group.items.length > PREVIEW_COUNT

                return (
                  <div key={key}>
                    {gallery.groupBy !== 'none' && (
                      <div className="flex items-center justify-between mb-2 px-1">
                        <div className="flex items-center gap-2">
                          {(gallery.groupBy === 'date' || gallery.groupBy === 'date-location') && (
                            <><Calendar className="w-3.5 h-3.5 text-gray-400" /><span className="text-sm font-semibold text-gray-700">{group.date}</span></>
                          )}
                          {gallery.groupBy === 'date-location' && <span className="text-gray-300">·</span>}
                          {(gallery.groupBy === 'location' || gallery.groupBy === 'date-location') && (
                            <><MapPin className="w-3.5 h-3.5 text-gray-400" /><span className="text-sm text-gray-500">{group.location}</span></>
                          )}
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{group.items.length}</span>
                        </div>
                        {hasMore && (
                          <button onClick={() => gallery.setExpandedGroups(prev => {
                            const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next
                          })} className="text-xs text-onrecord-orange hover:underline font-medium">
                            {isExpanded ? 'Show less' : `Show all ${group.items.length}`}
                          </button>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                      {visible.map(item => (
                        <div key={item.id} onClick={() => handleItemClick(item)}
                          className={`relative aspect-square cursor-pointer group rounded-lg overflow-hidden border-2 transition-all ${
                            selected.has(item.id) ? 'border-onrecord-orange ring-2 ring-onrecord-orange/30' : 'border-transparent hover:border-onrecord-orange'
                          }`}>
                          <img src={item.thumbnailUrl || item.mediaUrl} alt="" className="w-full h-full object-cover" />
                          {item.mediaType === 'video' && (
                            <div className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5"><Video className="w-3 h-3 text-white" /></div>
                          )}
                          {multiSelect && selected.has(item.id) && (
                            <div className="absolute top-1.5 left-1.5 w-5 h-5 bg-onrecord-orange rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-1.5">
                            {item.onRecordCode && <span className="text-white text-[9px] font-mono truncate">{item.onRecordCode}</span>}
                          </div>
                        </div>
                      ))}
                      {!isExpanded && hasMore && (
                        <button onClick={() => gallery.setExpandedGroups(prev => new Set(prev).add(key))}
                          className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-onrecord-orange hover:text-onrecord-orange transition-colors">
                          <span className="text-lg font-bold">+{group.items.length - PREVIEW_COUNT}</span>
                          <span className="text-[10px]">more</span>
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Multi-select action bar */}
        {multiSelect && selected.size > 0 && (
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 bg-white flex items-center justify-between">
            <span className="text-sm text-gray-600">{selected.size} selected</span>
            <button onClick={handleBatchSelect}
              className="px-6 py-2 bg-onrecord-orange text-white rounded-full text-sm font-semibold hover:bg-onrecord-orange-dark transition-colors">
              Select ({selected.size})
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
